import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  Paper,
  useMediaQuery,
  useTheme,
  Chip,
  Stack,
  Card,
  CardContent,
  Divider,

} from '@mui/material';
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SchoolIcon from '@mui/icons-material/School';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ChatIcon from '@mui/icons-material/Chat';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { trackPixelEvent, PixelEvents, trackHomePageCTAButton } from '../../utils/meta/metaPixel';
import CollegeScoreSearch from '../dashboard-components/CollegeScoreSearch';
import { CollegeSearchResult } from '../dashboard-components/ScoreProgressComparison';
import ButtonChoice from '../SkillPractice/ButtonChoice';
import { UserContext } from '../UserAndProfile/UserContext';
import { PromptContext } from '../PromptContext';
import DemoDiagnosticTest from '../TestPages/DemoDiagnosticTest';
import DemoDiagnosticTestResults from '../TestPages/DemoDiagnosticTestResults';
import { TestAttempt } from '../UserAndProfile/UserContext';


interface ActivePercentiles {
  '25th': boolean;
  '50th': boolean;
  '75th': boolean;
}

interface HeaderVariation {
  id: string;
  name: string;
  image: string;
  headline: string;
  subheadline: string;
  ctaText: string;
  alertChip?: string;
  alertIcon?: React.ReactNode;
  overlayStyle: string;
  textColor: string;
  benefits: string[];
}

const headerVariations: HeaderVariation[] = [
  {
    id: 'professional',
    name: 'Professional Focus',
    image: '/images/college-students-having-fun-1.jpg',
    headline: 'Increase SAT Scores 100-300 Points in 6 Weeks with AI SAT Tutoring ', //The AI-Powered SAT Prep That Delivers 100-300 Point Improvements in 6-8 Weeks
    subheadline: 'Trusted by 1,000+ students, teachers, and counselors, with a 100+ score improvement guarantee!',//research-backed,
    ctaText: 'Try for Free!', //Try Risk-Free, and Gift Free Trials to Your Students
    alertChip: '🎯 PROFESSIONAL GRADE',
    alertIcon: <EmojiEventsIcon />,
    overlayStyle: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 100%)',
    textColor: 'white',
    benefits: ['✓ Guaranteed 100+ Point Increase', '✓ Weekly Score Reports', '✓ Personalized & Fun!']
  }
  /*,
  {
    id: 'three-steps',
    name: '3-Step Program',
    image: '/images/college-students-having-fun-2.jpg',
    headline: 'The 3 Steps to Score 100-300+ Points & Get Into the BEST Colleges!',
    subheadline: 'Discover the Score, Plan, and Program Your Child NEEDS to improve SAT, earn scholarships, and gain admissions to top colleges!',
    ctaText: 'Start Your 3-Step Journey',
    alertChip: '🚀 PROVEN SYSTEM',
    alertIcon: <RocketLaunchIcon />,
    overlayStyle: 'linear-gradient(45deg, rgba(25,118,210,0.8) 0%, rgba(0,0,0,0.6) 100%)',
    textColor: 'white',
    benefits: ['✓ Step 1: Discover Score Needs', '✓ Step 2: Create Custom Plan', '✓ Step 3: Execute & Improve']
  }*/
];

const NewHomePage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useContext(UserContext);
  
  // Demo states
  const [selectedColleges, setSelectedColleges] = useState<CollegeSearchResult[]>([]);
  const [activePercentiles, setActivePercentiles] = useState<ActivePercentiles>({
    '25th': true,
    '50th': false,
    '75th': true,
  });
  //const [showAIDemo, setShowAIDemo] = useState(false);
  const [showDiagnosticDemo, setShowDiagnosticDemo] = useState(true);
  const [diagnosticResults, setDiagnosticResults] = useState<TestAttempt | null>(null);
  const [showResults, setShowResults] = useState(true);
  const [headerVariationIndex, setHeaderVariationIndex] = useState(0);

  // Create dummy data for Step 4 results preview
  const dummyDiagnosticResults: TestAttempt = {
    id: 'dummy-demo',
    testType: 'diagnostic',
    date: new Date().toISOString(),
    completed: true,
    sections: [{
      sectionNumber: 1,
      questions: [
        { questionIndex: 0, selectedAnswer: 'A', correctAnswer: 'A', isCorrect: true, skill: 'Quadratic Equations', difficulty: 'Easy', timeSpent: 45000 },
        { questionIndex: 1, selectedAnswer: 'C', correctAnswer: 'B', isCorrect: false, skill: 'Main Idea', difficulty: 'Medium', timeSpent: 62000 },
        { questionIndex: 2, selectedAnswer: 'B', correctAnswer: 'B', isCorrect: true, skill: 'Geometry', difficulty: 'Medium', timeSpent: 78000 },
        { questionIndex: 3, selectedAnswer: 'A', correctAnswer: 'B', isCorrect: false, skill: 'Tenses', difficulty: 'Hard', timeSpent: 55000 }
      ],
      elapsedTime: 240000
    }],
    mathScore: 650,
    readingWritingScore: 620,
    totalScore: 1270
  };

  // Track page view
  useEffect(() => {
    trackPixelEvent(PixelEvents.VIEW_CONTENT, {
      content_name: 'Ivan HomePage V2 View',
      content_category: 'Landing Page',
      content_type: 'Page View'
    });
  }, []);

  const handleCollegeSelect = (college: CollegeSearchResult) => {
    if (selectedColleges.length < 3) {
      setSelectedColleges([...selectedColleges, college]);
    }
  };

  const handlePercentileChange = (percentiles: ActivePercentiles) => {
    setActivePercentiles(percentiles);
  };

  const handleRemoveCollege = (index: number) => {
    setSelectedColleges(selectedColleges.filter((_, i) => i !== index));
  };

  const handleStartTrial = () => {
    const currentVariation = headerVariations[headerVariationIndex];
    trackHomePageCTAButton('NewHomePage', '/get-started', `Start Trial CTA - ${currentVariation.name}`);
    navigate('/get-started');
  };

  /*const handleTryAIDemo = () => {
    setShowAIDemo(true);
    trackPixelEvent('AITutorDemo', {
      content_name: 'AI Tutor Demo Started',
      content_category: 'Landing Page Engagement',
    });
  };*/



  const handleDiagnosticDemoComplete = (results: TestAttempt) => {
    setDiagnosticResults(results);
    setShowResults(true);
    setShowDiagnosticDemo(false);
    trackPixelEvent('DiagnosticDemoComplete', {
      content_name: 'Diagnostic Demo Completed',
      content_category: 'Landing Page Conversion',
    });
    
    setTimeout(() => {
      const resultsElement = document.getElementById('diagnostic-results-section');
      if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 500);
  };

  const handleExitDiagnosticDemo = () => {
    // Reset demo state to initial conditions
    setDiagnosticResults(null);
    setShowResults(true); // Ensure results are shown when demo resets
    // Force component to re-mount by toggling showDiagnosticDemo
    setShowDiagnosticDemo(false);
    setTimeout(() => setShowDiagnosticDemo(true), 100);
  };

  const handleMinimizeDiagnosticResults = () => {
    // Hide the results section
    setShowResults(false);
  };

  const handleShowResults = () => {
    // Show the results section again
    setShowResults(true);
  };





  const currentVariation = headerVariations[headerVariationIndex];

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      


      {/* Centered Hero Section */}
      <Box 
        sx={{ 
          position: 'relative',
          minHeight: { xs: '100vh', md: '85vh' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: `linear-gradient(180deg, ${theme.palette.custom.beige} 0%, ${theme.palette.secondary.main} 100%)`, //${theme.palette.custom.beigeLightContrast} 50%,
          overflow: 'hidden',
          textAlign: 'center',
          px: 2,
          pt: { xs: '80px', sm: '90px', md: '100px' }, // Account for nav bar height
          pb: { xs: 4, md: 6 },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '0px', //Revisit this and see if I can get the cool curve of blue overlapping the next section.
            background: 'white',
            borderRadius: '50% 50% 0 0 / 100% 100% 0 0',
            transform: 'scaleX(3)',
            zIndex: 1
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          {/* Centered Headlines */}
          <motion.div
            key={headerVariationIndex}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <Typography 
              variant="h2" 
              sx={{ 
                fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem', lg: '3.8rem' },
                fontWeight: 'bold',
                mb: { xs: 2, md: 3 },
                color: theme.palette.text.primary,
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
                maxWidth: '1600px',
                mx: 'auto'
              }}
            >
              {currentVariation.headline}
            </Typography>
            
            <Typography 
              variant="h4" 
              sx={{ 
                fontSize: { xs: '1rem', sm: '1.2rem', md: '1.4rem', lg: '1.6rem' },
                mb: { xs: 2, sm: 3, md: 4 },
                color: theme.palette.text.primary,
                fontWeight: '500',
                lineHeight: 1.4,
                maxWidth: '700px',
                mx: 'auto'
              }}
            >
              {currentVariation.subheadline}
            </Typography>

            {/* Benefits Chips */}
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2} 
              sx={{ mb: { xs: 2, sm: 3, md: 4 }, justifyContent: 'center', alignItems: 'center' }}
            >
              {currentVariation.benefits.map((benefit, index) => (
                                  <Chip
                    key={index}
                    label={benefit}
                    sx={{
                      bgcolor: theme.palette.primary.main,
                      color: 'white',
                      fontWeight: '600',
                      fontSize: {xs: '0.8rem', md: '1rem'},
                      py: 1,
                      px: 2,
                      border: `1px solid ${theme.palette.primary.dark}`,
                      '&:hover': {
                        bgcolor: theme.palette.primary.dark,
                        transform: 'translateY(-1px)',
                        boxShadow: theme.shadows[4]
                      }
                    }}
                  />
              ))}
            </Stack>
          </motion.div>

          {/* Centered CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleStartTrial}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  fontSize: '1.5rem',
                  py: 2,
                  px: 6,
                  borderRadius: 3,
                  background: theme.palette.primary.main,
                  '&:hover': {
                    background: theme.palette.primary.dark,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.3)'
                  }
                }}
              >
                {currentVariation.ctaText}
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* University Logos Section */}
      <Box sx={{ 
        bgcolor: 'grey.50', 
        py: { xs: 4, sm: 6 },
        borderTop: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography 
              variant="h2" 
              sx={{ 
                textAlign: 'center', 
                mb: 4,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 'bold'
              }}
            >
              Students Accepted Into Top Universities
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'row', 
              width: '100%',
              gap: { xs: 2, sm: 4 },
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              {[
                { src: '/images/CollegeIconsForLandingPage/Harvard_logo.png', alt: 'Harvard University' },
                { src: '/images/CollegeIconsForLandingPage/Yale_logo.png', alt: 'Yale University' },
                { src: '/images/CollegeIconsForLandingPage/Stanford_logo.png', alt: 'Stanford University' },
                { src: '/images/CollegeIconsForLandingPage/Princeton_logo.png', alt: 'Princeton University' },
                { src: '/images/CollegeIconsForLandingPage/MIT_logo.png', alt: 'MIT' }
              ].map((logo, index) => (
                <Box 
                  key={index}
                  sx={{ 
                    flex: { xs: '0 0 auto', sm: 1 }, 
                    display: 'flex', 
                    justifyContent: 'center',
                    opacity: 0.8,
                    '&:hover': { opacity: 1 },
                    transition: 'opacity 0.3s ease'
                  }}
                >
                  <img 
                    src={logo.src} 
                    alt={logo.alt}
                    style={{
                      height: isMobile ? '50px' : '70px', 
                      objectFit: 'contain',
                      filter: 'grayscale(20%)'
                    }} 
                  />
                </Box>
              ))}
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Enhanced Social Proof Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6 } }}>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Typography 
            variant="h2" 
            sx={{ 
              textAlign: 'center', 
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 'bold'
            }}
          >
            Real Students, Real Results
          </Typography>

          <Typography 
            variant="h5" 
            sx={{ 
              textAlign: 'center', 
              mb: 6,
              color: 'text.secondary',
              maxWidth: '600px',
              mx: 'auto'
            }}
          >
            See why families trust Next Step Education for SAT prep success
          </Typography>

          {/* Stats Row */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 4 }}>
            {[
              { stat: '1,000+', label: 'Students Helped', subtext: '', color: theme.palette.primary.main },
              { stat: 'Results Guaranteed', label: '', subtext: '', color: theme.palette.success.main },//Subtext 100+ Increase or Money Back
              { stat: '100-300+', label: 'Score Increase in 6-8 weeks', subtext: '', color: theme.palette.secondary.main },
              /*
              { stat: '1,000+', label: 'Students', subtext: '', color: theme.palette.primary.main },
              { stat: '50+', label: 'Schools Using Our Platform', subtext: '', color: theme.palette.secondary.main },
              { stat: '25+', label: 'Districts with Proven Results', subtext: '', color: theme.palette.success.main }
               */
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2, duration: 0.6 }}
                style={{ flex: 1 }} 
              >
                <Card sx={{ 
                  textAlign: 'center', 
                  p: {xs: 1, md: 2}, 
                  height: '100%',
                  background: `linear-gradient(135deg, ${item.color}10, ${item.color}05)`,
                  border: `2px solid ${item.color}20`
                }}>
                  <Typography 
                    variant="h1" 
                    sx={{ 
                      fontSize: { xs: '2rem', sm: '2.6rem' }, 
                      fontWeight: 'bold', 
                      color: item.color,
                      mb: 0.5 
                    }}
                  >
                    {item.stat}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.subtext}
                  </Typography>
                </Card>
              </motion.div>
            ))}
          </Box>

          {/* Enhanced Testimonials */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            gap: 3,
            position: 'relative'
          }}>
            {[
              { 
                quote: "It helped me be more prepared because I wasn't prepared at all before. Now I feel confident going into the test.", 
                author: "Jackie O.", 
                role: "Sophomore",
                before: "1180",
                after: "1380",
                improvement: "+200",
                image: "/images/best-sat-prep-student-next-step-education.png"
              },
              /*
              quote: "Next Step Education has been a game-changer for our district. Students who were struggling with SAT prep now have access to personalized, AI-powered tutoring that delivers real results.", 
                author: "Robbie Binnicker", 
                role: "Superintendent, Anderson School District One, SC",
                before: "",
                after: "",
                improvement: "PROFESSIONAL",
                image: "/images/founder-ivan-harjehausen.jpg"
              },
              { 
                quote: "As a teacher in NYC public schools, I've seen many SAT prep programs. This AI tutor provides the kind of individualized attention every student deserves, and the results speak for themselves.", 
                author: "Chris O'Sullivan", 
                role: "Teacher, New York City Public Schools",
                before: "",
                after: "",
                improvement: "EDUCATOR",
                image: "/images/founder-ivan-harjehausen.jpg"
              */
              { 
                quote: "My SAT score went up 220 points in 6 weeks! The AI tutor knew exactly where I was struggling and helped me improve.", 
                author: "Sarah J.", 
                role: "Junior",
                before: "1240",
                after: "1460",
                improvement: "+220",
                image: "/images/high-school-student-loves-next-step-education-sat-prep-1.png"
              },
              { 
                quote: "The personalized study plan was a game-changer. I finally understood concepts that confused me for months.", 
                author: "Kaleb B.", 
                role: "Junior",
                before: "1050",
                after: "1220",
                improvement: "+170",
                image: "/images/best-sat-prep-south-carolina.png"
              }
            ].map((testimonial, index) => (
              <Box key={index} sx={{ flex: 1, position: 'relative' }}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2, duration: 0.6 }}
                >
                  <Card sx={{ 
                    p: 2, 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'visible',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[8]
                    },
                    transition: 'all 0.3s ease'
                  }}>
                    {/* Score Improvement Badge */}
                    <Box sx={{ 
                      position: 'absolute',
                      top: -12,
                      right: 12,
                      bgcolor: testimonial.improvement === 'PROFESSIONAL' ? theme.palette.primary.main : 
                               testimonial.improvement === 'EDUCATOR' ? theme.palette.secondary.main : 
                               theme.palette.success.main,
                      color: 'white',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      boxShadow: theme.shadows[4],
                      zIndex: 10
                    }}>
                      {testimonial.improvement === 'PROFESSIONAL' ? 'SUPERINTENDENT' : 
                       testimonial.improvement === 'EDUCATOR' ? 'TEACHER' : 
                       testimonial.improvement}
                    </Box>

                    <Typography variant="body1" sx={{ 
                      fontStyle: 'italic', 
                      mb: 2.5, 
                      flex: 1,
                      fontSize: '1rem',
                      lineHeight: 1.4
                    }}>
                      "{testimonial.quote}"
                    </Typography>
                    
                    <Divider sx={{ mb: 1.5 }} />
                    
                    {/* Author Info with Scores */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box 
                        component="img"
                        src={testimonial.image}
                        alt={testimonial.author}
                        sx={{ 
                          width: 45, 
                          height: 45, 
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                          {testimonial.author}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                          {testimonial.role}
                        </Typography>
                      </Box>
                      {testimonial.before && testimonial.after && (
                        <Box sx={{ 
                          textAlign: 'right',
                          bgcolor: 'grey.50',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1
                        }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                            {testimonial.before} → {testimonial.after}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Card>
                </motion.div>
              </Box>
            ))}
          </Box>
        </motion.div>
      </Container>

      {/* College Score Search Demo Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8 } }}>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Typography 
            variant="h2" 
            sx={{ 
              textAlign: 'center', 
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 'bold'
            }}
          >
            Step 1: Set SAT Goals for Admissions Success!
          </Typography>
          {/*Alternatives:
          See What Scores Your Child Needs
          Set SAT Goals Based on Dream Colleges
           */}
          
          <Typography 
            variant="h5" 
            sx={{ 
              textAlign: 'center', 
              mb: 4,
              color: 'text.secondary',
              maxWidth: '700px',
              mx: 'auto'
            }}
          >
            Enter target colleges to see exactly what SAT scores are needed for admission
          </Typography>

          <Paper 
            elevation={8}
            sx={{p: { xs: 3, sm: 4 }, 
              borderRadius: 3,
              border: `2px solid ${theme.palette.primary.main}`,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <CollegeScoreSearch
              onCollegeSelect={handleCollegeSelect}
              onPercentileChange={handlePercentileChange}
              initialPercentiles={activePercentiles}
              maxColleges={3}
              selectedCount={selectedColleges.length}
            />

            {selectedColleges.length > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.5 }}
              >
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Your Selected Colleges:
                </Typography>
                <Stack spacing={2}>
                  {selectedColleges.map((college, index) => (
                    <Card key={index} variant="outlined">
                      <CardContent sx={{ py: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {college.name}
                          </Typography>
                          <Button 
                            size="small" 
                            onClick={() => handleRemoveCollege(index)}
                            color="error"
                          >
                            Remove
                          </Button>
                        </Box>
                        <Box sx={{ mt: 2, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {activePercentiles['25th'] && (
                            <Box>
                              <Typography variant="body2" color="text.secondary">25th Percentile</Typography>
                              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                Math: {college.mathScores['25th'] || 'N/A'} | 
                                R&W: {college.readingWritingScores['25th'] || 'N/A'}
                              </Typography>
                            </Box>
                          )}
                          {activePercentiles['50th'] && (
                            <Box>
                              <Typography variant="body2" color="text.secondary">50th Percentile</Typography>
                              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                Math: {college.mathScores['50th'] || 'N/A'} | 
                                R&W: {college.readingWritingScores['50th'] || 'N/A'}
                              </Typography>
                            </Box>
                          )}
                          {activePercentiles['75th'] && (
                            <Box>
                              <Typography variant="body2" color="text.secondary">75th Percentile</Typography>
                              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                Math: {college.mathScores['75th'] || 'N/A'} | 
                                R&W: {college.readingWritingScores['75th'] || 'N/A'}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </motion.div>
            )}
          </Paper>

          {selectedColleges.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography variant="h5" sx={{ mb: 2, color: theme.palette.primary.main, fontWeight: 'bold' }}>
                  Now see how our AI tutor helps students reach those scores →
                </Typography>
              </Box>
            </motion.div>
          )}
        </motion.div>
      </Container>

      

      {/* AI Tutor Demo Section 
      <Box sx={{ bgcolor: theme.palette.grey[50], py: { xs: 6, sm: 8 } }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Typography 
              variant="h2" 
              sx={{ 
                textAlign: 'center', 
                mb: 2,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 'bold'
              }}
            >
              Step 2: Experience Our AI Tutor
            </Typography>
            
            <Typography 
              variant="h5" 
              sx={{ 
                textAlign: 'center', 
                mb: 4,
                color: 'text.secondary',
                maxWidth: '700px',
                mx: 'auto'
              }}
            >
              Try 3 real SAT questions and see how our AI gives instant, personalized feedback
            </Typography>

            {!showAIDemo ? (
              <Box sx={{ textAlign: 'center' }}>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleTryAIDemo}
                    startIcon={<ChatIcon />}
                    sx={{
                      fontSize: '1.3rem',
                      py: 2,
                      px: 4,
                      borderRadius: 3,
                      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      '&:hover': {
                        background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                      }
                    }}
                  >
                    Try the AI Tutor Demo
                  </Button>
                </motion.div>
                
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
                  {[
                    { icon: <SchoolIcon />, text: 'Real SAT Questions' },
                    { icon: <ChatIcon />, text: 'Instant AI Feedback' },
                    { icon: <TrendingUpIcon />, text: 'Personalized Hints' }
                  ].map((feature, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ color: theme.palette.primary.main }}>{feature.icon}</Box>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {feature.text}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Paper elevation={8}
                  sx={{p: 2, bgcolor: 'white', borderRadius: 3, position: 'relative', overflow: 'hidden'}}
                >
                  <ButtonChoice 
                    demoMode={true}
                    demoSkill="Linear Equations"
                    demoDifficulty="Easy"
                  />
                </Paper>
              </motion.div>
            )}
          </motion.div>
        </Container>
      </Box>*/}

      {/* Step 3: Diagnostic Test Demo Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8 } }}>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Typography 
            variant="h2" 
            sx={{ 
              textAlign: 'center', 
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 'bold'
            }}
          >
            Step 2: Ask the AI Tutor for Guidance
          </Typography>
          
          <Typography variant="h5"
            sx={{textAlign: 'center', mb: 4, color: 'text.secondary', maxWidth: '700px', mx: 'auto'}}
          >
            Take 4 real SAT questions, ask the AI tutor for guidance, and get recommendations about what to study next!
          </Typography>

          {/* Feature bullets - always visible 
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
            {[
              { icon: <SchoolIcon />, text: '4 Real SAT Questions' },
              { icon: <ChatIcon />, text: 'AI Tutoring Feedback & Strategies' },
              { icon: <TrendingUpIcon />, text: 'Score Estimation' }
            ].map((feature, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ color: theme.palette.primary.main }}>{feature.icon}</Box>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {feature.text}
                </Typography>
              </Box>
            ))}
          </Box>*/}

          {/* Demo component - always displayed */}
          {showDiagnosticDemo && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Paper elevation={8}
                sx={{borderRadius: 3,
                  position: 'relative', overflow: 'hidden', minHeight: '600px'
                }}
              >
                <DemoDiagnosticTest 
                  onTestComplete={handleDiagnosticDemoComplete}
                  onExit={handleExitDiagnosticDemo}
                />
              </Paper>
            </motion.div>
          )}
        </motion.div>
      </Container>

      {/* Step 4: Diagnostic Results Section */}
      <Box sx={{ bgcolor: theme.palette.grey[50], py: { xs: 6, sm: 8 } }} id="diagnostic-results-section">
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h2" 
              sx={{textAlign: 'center', mb: 2, fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }, fontWeight: 'bold'}}
            >
              Step 3: See Your Detailed Results
            </Typography>
            
            {!showResults ? (
              // Minimized/collapsed view
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                  Results minimized
                </Typography>
                <Button 
                  variant="contained" 
                  onClick={handleShowResults}
                  sx={{ mb: 4 }}
                >
                  Show Results
                </Button>
              </Box>
            ) : (
              // Full results view
              <>
                <Typography variant="h5" 
                  sx={{ textAlign: 'center', mb: 4, color: 'text.secondary', maxWidth: '700px', mx: 'auto'}}
                >
                  {diagnosticResults 
                    ? "Here are your personalized diagnostic results!" 
                    : "Here's a preview of the insights you'll receive (try the demo above to see your real results)"}
                </Typography>

                <DemoDiagnosticTestResults
                  testAttempt={diagnosticResults || dummyDiagnosticResults}
                  onStartTrial={handleStartTrial}
                  onBackToDemo={handleExitDiagnosticDemo}
                  onMinimize={handleMinimizeDiagnosticResults}
                />
              </>
            )}
          </motion.div>
        </Container>
      </Box>

      {/* What Happens Next - Learning Journey Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8 } }}>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Typography 
            variant="h2" 
            sx={{ 
              textAlign: 'center', 
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 'bold'
            }}
          >
            Next Steps on the Learning Journey!
          </Typography>
          
          <Typography 
            variant="h5" 
            sx={{ 
              textAlign: 'center', 
              mb: 6,
              color: 'text.secondary',
              maxWidth: '800px',
              mx: 'auto'
            }}
          >
            The diagnostic test is just the beginning. Here's how we guide students to 100-300+ point improvements:
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {[
              {
                step: "4",
                title: "Personalized Study Plan Created",
                description: "Based on diagnostic results, our AI creates a custom 6-8 week study plan targeting each student's specific weak areas",
                features: ["Weekly goals and milestones", "Adaptive difficulty progression", "Time-optimized practice sessions"],
                icon: "📚",
                color: theme.palette.primary.main
              },
              {
                step: "5", 
                title: "Daily Skill Practice & AI Tutoring",
                description: "Students practice real SAT questions with instant AI feedback, explanations, and personalized hints",
                features: ["1000+ real SAT questions", "Step-by-step explanations", "24/7 AI tutor available"],
                icon: "🎯",
                color: theme.palette.secondary.main
              },
              {
                step: "6",
                title: "Progress Tracking & Parent Dashboard", 
                description: "Watch students improve week by week with detailed analytics and progress reports",
                features: ["Real-time score predictions", "Skill mastery tracking", "Parent progress emails"],
                icon: "📈",
                color: theme.palette.success.main
              },
              {
                step: "7",
                title: "Full Practice Tests & Final Prep",
                description: "Take full-length practice tests to solidify gains and ensure test-day readiness",
                features: ["4 complete practice tests", "Test-taking strategies", "Final score optimization"],
                icon: "🏆", 
                color: theme.palette.custom.amber
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2, duration: 0.6 }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', md: index % 2 === 0 ? 'row' : 'row-reverse' }, 
                  alignItems: 'center', 
                  gap: 4,
                  p: 3,
                  borderRadius: 3,
                  bgcolor: 'background.paper',
                  boxShadow: theme.shadows[4],
                  border: `2px solid ${item.color}20`
                }}>
                  {/* Icon and Step Number */}
                  <Box sx={{ 
                    flex: '0 0 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center'
                  }}>
                    <Box sx={{ 
                      fontSize: '4rem',
                      mb: 1
                    }}>
                      {item.icon}
                    </Box>
                    <Chip 
                      label={`Step ${item.step}`}
                      sx={{ 
                        bgcolor: item.color,
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1rem'
                      }}
                    />
                  </Box>

                  {/* Content */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" sx={{ 
                      mb: 2, 
                      fontWeight: 'bold',
                      color: item.color
                    }}>
                      {item.title}
                    </Typography>
                    
                    <Typography variant="body1" sx={{ 
                      mb: 3, 
                      fontSize: '1.1rem', 
                      lineHeight: 1.6,
                      color: 'text.secondary'
                    }}>
                      {item.description}
                    </Typography>

                    <Stack direction="column" spacing={1}>
                      {item.features.map((feature, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckIcon sx={{ color: item.color, fontSize: '1.2rem' }} />
                          <Typography variant="body2" sx={{ fontSize: '1rem' }}>
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              </motion.div>
            ))}
          </Box>

          {/* Call to Action */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography variant="h5" sx={{ 
              mb: 3, 
              fontWeight: 'bold',
              color: theme.palette.primary.main 
            }}>
              Complete Learning System = Guaranteed 100+ Point Improvement
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleStartTrial}
              sx={{
                fontSize: '1.2rem',
                py: 2,
                px: 4,
                borderRadius: 3
              }}
            >
              Start Your Complete Journey Today
            </Button>
          </Box>
        </motion.div>
      </Container>

      {/* Core Value Propositions Section 
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, sm: 8 } }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Typography 
              variant="h2" 
              sx={{ 
                textAlign: 'center', 
                mb: 2,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 'bold'
              }}
            >
              Why Parents & Educators Choose Next Step Education
            </Typography>
            
            <Typography 
              variant="h5" 
              sx={{ 
                textAlign: 'center', 
                mb: 6,
                color: 'text.secondary',
                maxWidth: '700px',
                mx: 'auto'
              }}
            >
              The only SAT prep that guarantees results with AI-powered personalized tutoring
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
              {/* Proven Results 
              <Box sx={{ flex: 1, width: '100%' }}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                >
                  <Card 
                    sx={{ 
                      height: '100%', 
                      textAlign: 'center', 
                      p: 4,
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: theme.shadows[8]
                      }
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      mb: 3,
                      '& svg': { 
                        fontSize: '4rem', 
                        color: theme.palette.primary.main 
                      }
                    }}>
                      <TrendingUpIcon />
                    </Box>
                    <Typography variant="h4" component="h3" sx={{ mb: 2, fontWeight: 'bold' }}>
                      Proven Results
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                      Over 1,000 students have improved their SAT scores by 100-300+ points using our AI-powered system.
                    </Typography>
                    <Box sx={{ 
                      bgcolor: theme.palette.primary.main, 
                      color: 'white', 
                      py: 1, 
                      px: 2, 
                      borderRadius: 2,
                      display: 'inline-block'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        100+ Point Guarantee
                      </Typography>
                    </Box>
                  </Card>
                </motion.div>
              </Box>

              {/* AI Personalization 
              <Box sx={{ flex: 1, width: '100%' }}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                >
                  <Card 
                    sx={{ 
                      height: '100%', 
                      textAlign: 'center', 
                      p: 4,
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: theme.shadows[8]
                      }
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      mb: 3,
                      '& svg': { 
                        fontSize: '4rem', 
                        color: theme.palette.secondary.main 
                      }
                    }}>
                      <ChatIcon />
                    </Box>
                    <Typography variant="h4" component="h3" sx={{ mb: 2, fontWeight: 'bold' }}>
                      AI Personalization
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                      Our AI adapts to your child's learning style, providing instant feedback and targeted practice.
                    </Typography>
                    <Box sx={{ 
                      bgcolor: theme.palette.secondary.main, 
                      color: 'white', 
                      py: 1, 
                      px: 2, 
                      borderRadius: 2,
                      display: 'inline-block'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        24/7 AI Tutor
                      </Typography>
                    </Box>
                  </Card>
                </motion.div>
              </Box>

              {/* Affordable Excellence 
              <Box sx={{ flex: 1, width: '100%' }}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                >
                  <Card 
                    sx={{ 
                      height: '100%', 
                      textAlign: 'center', 
                      p: 4,
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: theme.shadows[8]
                      }
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      mb: 3,
                      '& svg': { 
                        fontSize: '4rem', 
                        color: theme.palette.success.main 
                      }
                    }}>
                      <EmojiEventsIcon />
                    </Box>
                    <Typography variant="h4" component="h3" sx={{ mb: 2, fontWeight: 'bold' }}>
                      Affordable Excellence
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                      Get elite-level SAT prep for a fraction of the cost of traditional tutoring or prep courses.
                    </Typography>
                    <Box sx={{ 
                      bgcolor: theme.palette.success.main, 
                      color: 'white', 
                      py: 1, 
                      px: 2, 
                      borderRadius: 2,
                      display: 'inline-block'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        90% Less Cost
                      </Typography>
                    </Box>
                  </Card>
                </motion.div>
              </Box>
            </Box>
          </motion.div>
        </Container>
      </Box>*/}

      {/* Pedagogy & Research Section */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 6, sm: 8 } }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Typography 
              variant="h2" 
              sx={{ 
                textAlign: 'center', 
                mb: 2,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 'bold'
              }}
            >
              Research-Backed Excellence
            </Typography>
            
            <Typography 
              variant="h5" 
              sx={{ 
                textAlign: 'center', 
                mb: 6,
                color: 'text.secondary',
                maxWidth: '800px',
                mx: 'auto'
              }}
            >
              Built on proven educational research and 6+ years of specialized SAT tutoring and college guidance experience
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {/* Experience and Results 
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' }, 
                alignItems: 'center', 
                gap: 4 
              }}>
                <Box sx={{ flex: 1, order: { xs: 1, md: 2 } }}>
                  <Paper elevation={6} sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: theme.palette.primary.main }}>
                      6+ Years of Proven Results
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', lineHeight: 1.6 }}>
                      From teaching SAT prep and college guidance classes in high schools to providing one-on-one online tutoring, we've helped over 1,000 students improve their scores by 100-300 points and gain admission to their dream schools.
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 3, 
                      justifyContent: 'space-around',
                      mt: 'auto',
                      pt: 2,
                      borderTop: `1px solid ${theme.palette.divider}`
                    }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h3" color="primary" fontWeight="bold">100-300</Typography>
                        <Typography variant="body2" color="text.secondary">Point Increases</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h3" color="primary" fontWeight="bold">1,000+</Typography>
                        <Typography variant="body2" color="text.secondary">Students Helped</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h3" color="primary" fontWeight="bold">6+</Typography>
                        <Typography variant="body2" color="text.secondary">Years Experience</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Box>
                
                <Box sx={{ flex: 1, order: { xs: 2, md: 1 } }}>
                  <Box 
                    component="img"
                    src="/images/founder-ivan-harjehausen.jpg"
                    alt="Ivan Harjehausen teaching SAT prep"
                    sx={{ 
                      width: '100%', 
                      maxWidth: 400,
                      height: 'auto',
                      borderRadius: 3,
                      boxShadow: theme.shadows[8],
                      border: `3px solid ${theme.palette.primary.main}`
                    }}
                  />
                </Box>
              </Box>*/}

              {/* Research Methodology */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' }, 
                alignItems: 'center', 
                gap: 4 
              }}>
                <Box sx={{ flex: 2 }}>
                  <Paper elevation={6} sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: theme.palette.secondary.main }}>
                      Scientific Learning Approach
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.6 }}>
                      Our SAT program isn't just another test prep course. It's built on extensive research in cognitive science, educational psychology, and proven learning techniques.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', lineHeight: 1.6 }}>
                      We've analyzed thousands of SAT questions, studied the most effective learning methods, and incorporated insights from leading educational researchers to create a program that delivers results efficiently.
                    </Typography>
                    
                    <Box sx={{ mt: 'auto' }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.text.primary }}>
                        Key Research Principles:
                      </Typography>
                      <Stack spacing={1}>
                        {[
                          'Spaced repetition for long-term retention',
                          'Active recall and retrieval practice',
                          'Adaptive difficulty progression',
                          'Immediate feedback and error correction',
                          'Metacognitive strategy training'
                        ].map((principle, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckIcon sx={{ color: theme.palette.success.main, fontSize: '1.2rem' }} />
                            <Typography variant="body2" sx={{ fontSize: '1rem' }}>
                              {principle}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </Paper>
                </Box>
                
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                  <Box 
                    component="img"
                    src="/images/homepage/book-influences.png"
                    alt="Research books and influences behind our SAT methodology"
                    sx={{ 
                      maxWidth: '100%', 
                      maxHeight: 500,
                      borderRadius: 3,
                      boxShadow: theme.shadows[6],
                      border: `1px solid ${theme.palette.divider}`
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Prominent FAQ Section */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, sm: 8 } }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Typography 
              variant="h2" 
              sx={{ 
                textAlign: 'center', 
                mb: 2,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 'bold'
              }}
            >
              Frequently Asked Questions
            </Typography>
            
            <Typography 
              variant="h5" 
              sx={{ 
                textAlign: 'center', 
                mb: 6,
                color: 'text.secondary',
                maxWidth: '600px',
                mx: 'auto'
              }}
            >
              Here are the most important questions we hear from educators, students, and families
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: '1200px', mx: 'auto' }}>
              {[
                {
                  question: "Can this really be as good as a human Ivy League tutor?",
                  answer: "For most students, yes, and often better. Our program is based on years of live tutoring experience with proven strategies. The AI tutor offers instant, personalized support 24/7, and unlike a person, it never gets tired or impatient. Some students may benefit from additional motivation, but for learning material and improving scores, our AI tutor delivers results."
                },
                {
                  question: "How fast will students see results?",
                  answer: "Many students see noticeable improvement in just 3-4 weeks. Students who practice 3-4 times per week for 20+ minutes typically make significant gains within 6-8 weeks. Consistent, short sessions work far better than cramming - repetition helps lessons stick and builds momentum."
                },
                {
                  question: "Does this work for students aiming for top scores (1400+)?",
                  answer: "Absolutely. The program adapts as students improve, offering higher-difficulty questions and advanced strategies. For students aiming for 1400+, the AI tutor provides targeted support, mistake-reduction strategies, and challenging questions to push scores even higher."
                },
                {
                  question: "How does this fit into busy student schedules?",
                  answer: "Students practice anywhere - during breakfast, waiting for the bus, in study hall, before sports practice, or as a 10-15 minute bedtime routine. The mobile-friendly platform means students can improve their SAT score in any spare moment, no matter how busy their schedule."
                }
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                >
                  <Paper elevation={3} sx={{ p: 4, borderLeft: `4px solid ${theme.palette.primary.main}` }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.text.primary }}>
                      {faq.question}
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, color: 'text.secondary' }}>
                      {faq.answer}
                    </Typography>
                  </Paper>
                </motion.div>
              ))}
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Professional Credibility Section */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 4, sm: 6 } }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                textAlign: 'center', 
                mb: 4,
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                fontWeight: 'bold',
                color: theme.palette.primary.main
              }}
            >
              Trusted by Educational Professionals
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' }, 
              gap: 3, 
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center'
            }}>
              {[
                'Developed by SAT Expert with 6+ Years',
                'Used by Teachers and Counselors in Schools Nationwide', 
                'Students Start in 30 Seconds, Mobile-Friendly'
              ].map((credential, index) => (
                <Box key={index} sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 'bold', 
                    color: theme.palette.text.primary,
                    fontSize: { xs: '1rem', sm: '1.1rem' }
                  }}>
                    {credential}
                  </Typography>
                </Box>
              ))}
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Founder Story Section */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 6, sm: 8 } }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Typography 
              variant="h2" 
              sx={{ 
                textAlign: 'center', 
                mb: 6,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 'bold'
              }}
            >
              Meet Your Founder
            </Typography>

            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' }, 
              gap: 6, 
              alignItems: 'center' 
            }}>
              {/* Founder Image */}
              <Box sx={{ flex: { md: 1 }, textAlign: 'center' }}>
                <Box 
                  component="img"
                  src="/images/founder-ivan-harjehausen.jpg"
                  alt="Ivan Harjehausen, Founder of Next Step Education"
                  sx={{ 
                    width: { xs: 250, sm: 300, md: 350 }, 
                    height: { xs: 250, sm: 300, md: 350 },
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: `6px solid ${theme.palette.primary.main}`,
                    boxShadow: theme.shadows[8]
                  }}
                />
              </Box>

              {/* Founder Story */}
              <Box sx={{ flex: { md: 1.5 } }}>
                <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: theme.palette.primary.main }}>
                  Ivan Harjehausen
                </Typography>
                
                <Typography variant="h6" sx={{ mb: 3, color: 'text.secondary', fontStyle: 'italic' }}>
                  SAT Expert & AI Education Pioneer
                </Typography>

                <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.6 }}>
                  "After helping over 1,000 students improve their SAT scores by an average of 220 points, 
                  I realized traditional tutoring wasn't scalable or affordable for most families. That's when 
                  I created Next Step Education's AI-powered platform."
                </Typography>

                <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', lineHeight: 1.6 }}>
                  "Our AI tutor combines my years of SAT expertise with cutting-edge technology to provide 
                  every student with personalized, 24/7 support. It's like having a master SAT tutor 
                  available whenever students need help."
                </Typography>

                <Box sx={{ 
                  bgcolor: theme.palette.primary.main, 
                  color: 'white', 
                  p: 3, 
                  borderRadius: 2
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white', mb: 1 }}>
                    My Promise to You:
                  </Typography>
                  <Typography variant="body1">
                    Students will improve by 100+ points, or we'll refund your money back. That's our guarantee. 
                  </Typography>
                </Box>
              </Box>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box sx={{ 
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        color: 'white',
        py: { xs: 8, sm: 10 }
      }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Typography 
              variant="h1" 
              sx={{ 
                textAlign: 'center', 
                mb: 3,
                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                fontWeight: 'bold',
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              Ready to See Results?
            </Typography>

            <Typography 
              variant="h4" 
              sx={{ 
                textAlign: 'center', 
                mb: 4,
                fontSize: { xs: '1.3rem', sm: '1.6rem' },
                opacity: 0.9,
                maxWidth: '700px',
                mx: 'auto',
                color: 'white'
              }}
            >
              Start your 14-day free trial. No credit card required.
            </Typography>

            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleStartTrial}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    fontSize: '1.5rem',
                    py: 2,
                    px: 6,
                    borderRadius: 3,
                    bgcolor: 'white',
                    color: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.3)'
                    }
                  }}
                >
                  Start 14-Day Free Trial
                </Button>
              </motion.div>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
              {[
                'Full access for 14 days',
                'Cancel anytime',
                '100+ point improvement guarantee'
              ].map((benefit, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckIcon />
                  <Typography variant="body1" sx={{ color: 'white', fontWeight: '500' }}>{benefit}</Typography>
                </Box>
              ))}
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default NewHomePage; 